import {
	apiClient,
	formatter,
	Message,
	Table,
} from "@ruter-as/web-components-and-tools";
import type React from "react";
import { useEffect, useState } from "react";
import {
	mapProductTemplateArray,
	type ProductTemplate,
} from "../../types/ProductTemplate";

const CompanyTicketInfoPage: React.FC = () => {
	const [productTemplates, setProductTemplates] = useState<ProductTemplate[]>();
	const [ticketError, setTicketError] = useState(false);

	useEffect(() => {
		const fetchPrices = async (): Promise<void> => {
			const productTemplateResponse = await apiClient.get(
				"/api/product/list-v2",
				mapProductTemplateArray,
			);
			if (productTemplateResponse.type === "success") {
				setProductTemplates(productTemplateResponse.result);
			} else {
				setTicketError(true);
			}
		};
		fetchPrices();
	}, []);

	const thirtyDays = productTemplates
		?.find((productTemplate) =>
			productTemplate.tags.find(
				(x) => x === "bigcustomer:subscriptionComparisonTicket",
			),
		)
		?.products.filter((x) => x.profileId === "ADULT");
	const companyTickes = productTemplates
		?.find((productTemplate) =>
			productTemplate.tags.find((x) => x === "bigcustomer:subscriptionTicket"),
		)
		?.products.filter((x) => x.profileId === "ADULT");

	let normal1ZonePrice = 0;
	let normal1ZoneSpecialPrice = 0;
	let normal2ZonePrice = 0;
	let normalAllZonePrice = 0;
	let company1ZonePrice = 0;
	let company1ZoneSpecialPrice = 0;
	let company2ZonePrice = 0;
	let companyAllZonePrice = 0;

	if (thirtyDays) {
		normal1ZonePrice =
			thirtyDays
				.find((x) => x.filters === null)
				?.prices.find((x) => x.nrOfZones === 1)?.priceInclVAT || 0;
		normal1ZoneSpecialPrice =
			thirtyDays
				.find((x) => x.filters !== null)
				?.prices.find((x) => x.nrOfZones === 1)?.priceInclVAT || 0;
		normal2ZonePrice =
			thirtyDays
				.find((x) => x.filters === null)
				?.prices.find((x) => x.nrOfZones === 2)?.priceInclVAT || 0;
		normalAllZonePrice =
			thirtyDays
				.find((x) => x.filters === null)
				?.prices.find((x) => x.nrOfZones === 3)?.priceInclVAT || 0;
	}
	if (companyTickes) {
		company1ZonePrice =
			companyTickes
				.find((x) => x.filters === null)
				?.prices.find((x) => x.nrOfZones === 1)?.priceInclVAT || 0;
		company1ZoneSpecialPrice =
			companyTickes
				.find((x) => x.filters !== null)
				?.prices.find((x) => x.nrOfZones === 1)?.priceInclVAT || 0;
		company2ZonePrice =
			companyTickes
				.find((x) => x.filters === null)
				?.prices.find((x) => x.nrOfZones === 2)?.priceInclVAT || 0;
		companyAllZonePrice =
			companyTickes
				.find((x) => x.filters === null)
				?.prices.find((x) => x.nrOfZones === 3)?.priceInclVAT || 0;
	}

	const priceRows = [
		{ label: "1 sone", company: company1ZonePrice, normal: normal1ZonePrice },
		{
			label: "Én sone (sone 1)",
			company: company1ZoneSpecialPrice,
			normal: normal1ZoneSpecialPrice,
		},
		{ label: "2 soner", company: company2ZonePrice, normal: normal2ZonePrice },
		{
			label: "Alle soner",
			company: companyAllZonePrice,
			normal: normalAllZonePrice,
		},
	].map(({ label, company, normal }) => ({
		key: label,
		cells: [
			label,
			{
				content: formatter.number.currency(Math.floor(company / 12) || 0),
				className: "price",
			},
			{
				content: formatter.number.currency(normal || 0),
				className: "old-price",
			},
		],
	}));

	return (
		<div data-test-id="info-page">
			<br />
			<h2>Slik fungerer bedriftsbilletten</h2>
			<br />
			<p>
				Vi heier på de som reiser kollektivt til og fra jobb og møter. Derfor
				tilbyr vi deg en billett som er rimeligere per måned enn en ordinær
				30-dagersbillett.
			</p>
			<p>
				Du får billetten i Ruter-appen og den fungerer som et abonnement. Du
				slipper å huske å fornye billetten - det går automatisk.
			</p>
			<h3>Hvordan fungerer billetten?</h3>
			<ul>
				<li>
					Billetten er en løpende månedsbillett. Det vil si at man kan bestille
					den fra den 1. i ønsket måned og den vil være gyldig frem til man
					eventuelt ønsker å si opp avtalen.
				</li>
				<li>
					Billetten er gyldig som en ordinær Ruter-billett, på buss, trikk,
					T-bane, båt og tog (ikke Flytoget) for ønskede soner i Oslo og
					Akershus
				</li>
				<li>
					Se også vilkår -{" "}
					<a href="https://ruter.no/fa-hjelp/vilkar/personvern/avtale-ruter-profil/vilkar-bedriftsbillett/">
						https://ruter.no/fa-hjelp/vilkar/personvern/avtale-ruter-profil/vilkar-bedriftsbillett/
					</a>
				</li>
			</ul>

			<h3>
				Betaling for billetten skjer gjennom lønnstrekk eller subsidiering fra
				arbeidsgiver
			</h3>
			<ul>
				<li>Betaling for billetten skjer gjennom lønnstrekk.</li>
				<li>
					Tilhører du et borettslag som tilbyr billetten vil du få faktura fra
					borettslaget
				</li>
				<li>
					Billetten har en månedspris som er ca. 17% lavere pr måned enn ordinær
					30-dagersbillett (Se priser nedenfor){" "}
				</li>
			</ul>

			<h2>Prisliste</h2>
			{!ticketError && (
				<Table
					loading={!productTemplates}
					data-test-id="price-list"
					headerCells={[
						"Antall soner",
						{ content: "Månedspris bedriftsbillett", className: "price" },
						{ content: "Ordinær 30-dagersbillett", className: "price" },
					]}
					rows={priceRows}
				/>
			)}
			{ticketError && (
				<Message skin="danger" data-test-id="price-error">
					Klarte ikke å hente prisliste
				</Message>
			)}

			<h2>Ofte stilte spørsmål: </h2>
			<h3>Hvordan får jeg billetten:</h3>
			<ul>
				<li>
					Hvis det står &quot;Bestill bedriftsbillett&quot; øverst i
					bestillingsskjemaet vil du motta en SMS med link til billetten din når
					du har sendt inn bestillingen.
				</li>
				<li>
					Hvis det står &quot;Søk om bedriftsbillett&quot; øverst i
					bestillingsskjemaet blir det sendt en henvendelse til administrator i
					din bedrift/ditt borettslag. Når bestillingen er godkjent av
					administrator vil du motta en SMS med link til din billett.
				</li>
				<li>
					For å hente billetten må du ha Ruter-appen og være innlogget med Ruter
					profil, med samme telefonnummer som billetten er registrert på.
				</li>
				<li>
					Når du mottar SMS med hentekode, trykker du på linken og får den opp i
					Ruter-appen. Alternativt kan du også taste inn hentekoden manuelt i
					appen.
				</li>
			</ul>

			<h3>Hvor finner jeg min billett:</h3>
			<ul>
				<li>
					Når du har logget inn i Ruter-appen og hentet billetten, vil den ligge
					i Ruter-appen som andre billetter.
				</li>
			</ul>

			<h3>Hvordan fungerer billetten:</h3>
			<ul>
				<li>Billetten fungerer som en ordinær periodebillett fra Ruter</li>
				<li>
					Billetten kan brukes på Ruters busser, trikker, T-baner og båter, og
					på Vys tog for de sonene du har valgt. Se sonekart:{" "}
					<a
						target="_blank"
						rel="noopener noreferrer"
						href="https://ruter.no/kjop-billett/soner/?sq=sonekart"
					>
						https://ruter.no/kjop-billett/soner/?sq=sonekart
					</a>
				</li>
			</ul>

			<h3>Hvordan endrer jeg sone på billetten:</h3>
			<ul>
				<li>
					Endring av sone kan gjøres via denne linken distribuert av bedriften
					din (fra mars 2025). Alternativt er å melde endringer til bedriftens
					administrator av Ruter Bedrift.
				</li>
				<li>Endring av soner trer i kraft den 1. i måneden. </li>
			</ul>

			<h3>Hvordan flytte billetten til ny telefon:</h3>
			<ul>
				<li>
					Logg inn i Ruter appen med samme telefonnummer på ny telefon. Trykk
					inn på billetten som står som ikke gyldig på denne enheten. Du får da
					valget om å flytte billetten.{" "}
				</li>
				<li>
					<a
						target="_blank"
						rel="noopener noreferrer"
						href="https://ruter.no/fa-hjelp/overfore-billetter/"
					>
						https://ruter.no/fa-hjelp/overfore-billetter/
					</a>
				</li>
				<li>Det er mulig å overføre billetten 10 ganger totalt</li>
			</ul>

			<h3>Hvordan sier jeg opp billetten:</h3>
			<ul>
				<li>Billetten kan du si opp via denne linken utgitt av din bedrift.</li>
			</ul>

			<h3>Er det bindingstid på billetten:</h3>
			<ul>
				<li>
					Det er ingen bindingstid på bedriftsavtalen utover at man må fullføre
					den måneden som er påbegynt.
				</li>
			</ul>
		</div>
	);
};

export default CompanyTicketInfoPage;
